import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { Link as ScrollLink } from 'react-scroll';
import { FaBars, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import LanguageSelector from './LanguageSelector';

/**
 * NavbarCustom - clean rewrite
 */
export default function NavbarCustom({ linksDisabled = false, hideLinks = false, homeLink = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const isEcommerce = /^\/(es|en)\/ecommerce(\/|$)/.test(location.pathname);

  const { t } = useTranslation();

  const navItems = [
    { key: 'technologies', labelKey: 'tecnologies', to: 'tecnologias', offset: -60 },
    { key: 'projects', labelKey: 'projects', to: 'proyectos', offset: -60 },
    { key: 'contact', labelKey: 'contact', to: 'contacto', offset: 1500 },
  ];

  const [mobileOpen, setMobileOpen] = useState(false);
  const firstNavRef = useRef(null);

  const transition = reduceMotion ? { duration: 0 } : { duration: 0.28, ease: 'easeOut' };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1200px)');
    const handler = (e) => { if (e.matches) setMobileOpen(false); };
    try { mql.addEventListener('change', handler); } catch (err) { mql.addListener(handler); }
    return () => { try { mql.removeEventListener('change', handler); } catch (err) { mql.removeListener(handler); } };
  }, []);

  const onLogoClick = useCallback(() => {
    const match = location.pathname.match(/^\/(es|en)/);
    const lang = (match && match[1]) || 'en';
    const home = homeLink || `/${lang}`;
    navigate(home);
    setMobileOpen(false);
  }, [homeLink, location.pathname, navigate]);

  const renderNav = (item, idx) => {
    if (!isEcommerce && !hideLinks && !linksDisabled) {
      return (
        <ScrollLink
          key={item.key}
          to={item.to}
          spy
          smooth
          offset={item.offset}
          duration={300}
          className="nav-link"
          onClick={() => setMobileOpen(false)}
          innerRef={idx === 0 ? firstNavRef : undefined}
        >
          {t(item.labelKey || item.label)}
        </ScrollLink>
      );
    }

    if (!isEcommerce && !hideLinks && linksDisabled) {
      return (
        <span key={item.key} className="nav-link disabled" aria-disabled="true" tabIndex={-1}>{t(item.labelKey || item.label)}</span>
      );
    }

    return null;
  };

  return (
    <Navbar bg="light" expand="xl" fixed="top" className="navbar-custom">
      <Container className="navbar__container d-flex align-items-center">
        <div
          className="navbar__logo-container"
          role="button"
          tabIndex={0}
          onClick={onLogoClick}
          onKeyDown={(e) => { if (e.key === 'Enter') onLogoClick(); }}
          aria-label="Home"
          style={{ cursor: 'pointer' }}
        >
          <img className="navbar__logo" src={`${process.env.PUBLIC_URL}/img/desktop/logo-1.png`} alt="Logo" />
        </div>

        <Nav className="ms-auto d-none d-xl-flex align-items-center">
          {navItems.map((it, i) => (
            <React.Fragment key={it.key}>{renderNav(it, i)}</React.Fragment>
          ))}

          <div style={{ marginLeft: 16 }}>
            <LanguageSelector />
          </div>
        </Nav>

        <div className="d-xl-none ms-auto d-flex align-items-center">
          <div style={{ marginRight: 8 }}>
            <LanguageSelector />
          </div>

          {!isEcommerce && (
            <button
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              className="navbar__toggle btn btn-link"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>
          )}
        </div>

        <div className="w-100 d-xl-none">
          <AnimatePresence>
            {mobileOpen && (
              <motion.div
                key="mobile-menu"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={transition}
                style={{ overflow: 'hidden' }}
                className="navbar-mobile-menu"
              >
                <nav style={{ padding: '12px 0' }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {navItems.map((it, idx) => (
                      <li key={it.key} style={{ padding: '8px 0' }}>
                        {renderNav(it, idx)}
                      </li>
                    ))}
                  </ul>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Container>
    </Navbar>
  );
}

NavbarCustom.propTypes = {
  linksDisabled: PropTypes.bool,
  hideLinks: PropTypes.bool,
  homeLink: PropTypes.string,
};

NavbarCustom.defaultProps = {
  linksDisabled: false,
  hideLinks: false,
  homeLink: null,
};
